import React, { useState } from "react";
import styled from "styled-components";
import { Player, GameRole } from "../../types";
import { dictionaries } from "../../utils/dictionary";
import {
  ModalOverlay,
  ModalCard,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalTitle,
  ModalCloseButton,
  PhaseButton,
} from "../../styles/SharedStyles";
import { theme } from "../../styles/theme";

interface GameActionsProps {
  language: number;
  players: Player[];
  playerRoles: GameRole[];
  playersLeaved: number[];
  onDisqualifyPlayer: (playerIndex: number) => void;
  onPPK: (playerIndex: number, winningTeam: "red" | "black") => void;
  onClose: () => void;
}

type ActionType = "disqualify" | "ppk" | null;

const ActionSelectionGrid = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: center;
  margin-top: ${theme.spacing.md};
`;

const ActionButton = styled(PhaseButton)`
  flex: 1;
  max-width: 200px;
  padding: 1rem 1.5rem;
  font-size: 1rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.85rem 1.25rem;
    font-size: 0.9rem;
  }
`;

const ColoredModalHeader = styled(ModalHeader)<{
  $variant: "warning" | "danger";
}>`
  background: ${({ $variant }) => {
    if ($variant === "danger")
      return "linear-gradient(135deg, rgba(255, 77, 109, 0.25), rgba(200, 50, 80, 0.25))";
    return "linear-gradient(135deg, rgba(255, 196, 0, 0.25), rgba(200, 150, 0, 0.25))";
  }};
  border-bottom: 1px solid
    ${({ $variant }) => {
      if ($variant === "danger") return "rgba(255, 77, 109, 0.35)";
      return "rgba(255, 196, 0, 0.35)";
    }};
  padding-bottom: 1.5rem;
`;

const InstructionText = styled.p`
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.text.secondary};
  font-size: 0.95rem;
`;

const PlayerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${theme.spacing.sm};
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const PlayerCard = styled.button.attrs({ type: "button" })<{
  $selected: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.lg};
  background: ${({ $selected }) =>
    $selected
      ? "linear-gradient(135deg, rgba(124, 93, 255, 0.25), rgba(70, 46, 150, 0.25))"
      : "rgba(255, 255, 255, 0.05)"};
  border: 2px solid
    ${({ $selected }) =>
      $selected ? theme.colors.border.strong : theme.colors.border.secondary};
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease;
  min-height: 120px;
  will-change: transform;

  &:hover {
    transform: translateY(-2px);
    background: ${({ $selected }) =>
      $selected
        ? "linear-gradient(135deg, rgba(124, 93, 255, 0.35), rgba(70, 46, 150, 0.35))"
        : "rgba(255, 255, 255, 0.12)"};
  }
`;

const PlayerAvatar = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid ${theme.colors.border.secondary};
`;

const PlayerNumber = styled.div`
  font-weight: 700;
  font-size: 1.125rem;
  color: ${theme.colors.text.primary};
`;

const PlayerName = styled.div`
  font-size: 0.85rem;
  color: ${theme.colors.text.secondary};
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

const AlertBox = styled.div<{ $variant: "info" | "warning" }>`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  background: ${({ $variant }) => {
    if ($variant === "warning") return theme.colors.card.instruction;
    return "rgba(68, 255, 210, 0.12)";
  }};
  border: 1px solid
    ${({ $variant }) => {
      if ($variant === "warning") return "rgba(255, 196, 0, 0.35)";
      return "rgba(68, 255, 210, 0.35)";
    }};
  margin-bottom: ${theme.spacing.lg};

  strong {
    display: inline;
    margin-right: ${theme.spacing.sm};
    font-weight: 700;
    color: ${theme.colors.text.primary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: flex-end;
`;

const GameActions: React.FC<GameActionsProps> = ({
  language,
  players,
  playerRoles,
  playersLeaved,
  onDisqualifyPlayer,
  onPPK,
  onClose,
}) => {
  const [actionType, setActionType] = useState<ActionType>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<number | null>(null);

  const dict = dictionaries[language].ph;

  const getAlivePlayers = () => {
    return players.filter((_, index) => !playersLeaved.includes(index + 1));
  };

  const getPlayers = () => {
    return actionType === "disqualify"
              ? getAlivePlayers()
              : players;
  };

  const getPlayerRole = (index: number): GameRole => {
    return playerRoles[index] || "red";
  };

  const handlePlayerSelect = (playerIndex: number) => {
    setSelectedPlayer(playerIndex);
  };

  const handleConfirmAction = () => {
    if (selectedPlayer === null) return;

    if (actionType === "disqualify") {
      onDisqualifyPlayer(selectedPlayer);
    } else if (actionType === "ppk") {
      const playerRole = getPlayerRole(selectedPlayer);
      const winningTeam =
        playerRole === "black" || playerRole === "don" ? "red" : "black";
      onPPK(selectedPlayer, winningTeam);
    }

    onClose();
  };

  const getActionDescription = (): string => {
    if (selectedPlayer == null || actionType === null) return "";

    const playerName = players[selectedPlayer].nickname;
    const playerNumber = selectedPlayer + 1;

    if (actionType === "disqualify") {
      return `${dict.disqualify} ${playerNumber}. ${playerName}`;
    } else if (actionType === "ppk") {
      const playerRole = getPlayerRole(selectedPlayer);
      const winningTeam =
        playerRole === "black" || playerRole === "don" ? "red" : "black";
      return winningTeam === "red"
        ? `${dict.ppkred} ${playerNumber}`
        : `${dict.ppkblack} ${playerNumber}`;
    }

    return "";
  };

  if (actionType === null) {
    return (
      <ModalOverlay>
        <ModalCard>
          <ModalHeader>
            <ModalTitle>{dict.chooseact}</ModalTitle>
            <ModalCloseButton onClick={onClose}>×</ModalCloseButton>
          </ModalHeader>

          <ModalBody>
            <InstructionText>{dict.select_action}</InstructionText>
            <ActionSelectionGrid>
              <ActionButton
                $variant="primary"
                onClick={() => {
                  setSelectedPlayer(null);
                  setActionType("disqualify");
                }}
              >
                {dict.disqualify}
              </ActionButton>
              <ActionButton
                $variant="primary"
                onClick={() => {
                  setSelectedPlayer(null);
                  setActionType("ppk");
                }}
              >
                {dict.ppk}
              </ActionButton>
            </ActionSelectionGrid>
          </ModalBody>

          <ModalFooter>
            <PhaseButton $variant="secondary" onClick={onClose}>
              {dict.cncl}
            </PhaseButton>
          </ModalFooter>
        </ModalCard>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay>
      <ModalCard>
        <ColoredModalHeader
          $variant={actionType === "disqualify" ? "warning" : "danger"}
        >
          <ModalTitle>
            {actionType === "disqualify" ? dict.disqualify : dict.ppk}
          </ModalTitle>
          <ModalCloseButton onClick={onClose}>×</ModalCloseButton>
        </ColoredModalHeader>

        <ModalBody>
          <InstructionText>
            {actionType === "disqualify"
              ? dict.select_disqualify
              : dict.select_ppk}
          </InstructionText>

          <PlayerGrid>
            {getPlayers().map((player) => {
              const originalIndex = players.findIndex(
                (p) => p.user_id === player.user_id
              );
              const playerNumber = originalIndex + 1;
              const isSelected = selectedPlayer === originalIndex;

              return (
                <PlayerCard
                  key={player.user_id}
                  $selected={isSelected}
                  onClick={() => handlePlayerSelect(originalIndex)}
                >
                  <PlayerAvatar
                    src={player.avatar}
                    alt={dict.player_avatar_alt.replace(
                      "{0}",
                      player.nickname
                    )}
                  />
                  <PlayerNumber>{playerNumber}</PlayerNumber>
                  <PlayerName>{player.nickname}</PlayerName>
                </PlayerCard>
              );
            })}
          </PlayerGrid>

          {selectedPlayer !== null && (
            <AlertBox $variant="info">
              <strong>{dict.action_label}</strong> {getActionDescription()}
            </AlertBox>
          )}

          {actionType === "ppk" && (
            <AlertBox $variant="warning">
              <strong>{dict.note_label}</strong> {dict.ppk_note}
            </AlertBox>
          )}
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            <PhaseButton
              $variant="secondary"
              onClick={() => setActionType(null)}
            >
              {dict.back}
            </PhaseButton>
            <PhaseButton
              $variant={actionType === "disqualify" ? "primary" : "danger"}
              onClick={handleConfirmAction}
              disabled={selectedPlayer === null}
            >
              {dict.confirm}
            </PhaseButton>
          </ButtonGroup>
        </ModalFooter>
      </ModalCard>
    </ModalOverlay>
  );
};

export default GameActions;
